import { createFileRoute, useRouter } from "@tanstack/react-router";

import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import DEVICE_CONTEXT from "src/context/internal/device";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "src/components/base/description-list";
import { Button } from "src/components/base/button";
import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { notify } from "src/components/base/toast";
import SETTINGS_CONTEXT from "src/context/internal/settings";
import { Subheading } from "src/components/base/heading";
import { SimpleDeviceGroup, SimpleTag } from "src/api/generated/frontend_internal_v1";
import { ComboboxOption, StatelessCombobox } from "src/components/base/combobox";
import { Field, Label } from "src/components/base/fieldset";
import CreateTagDialog from "src/components/dialogs/create-tag";
import { Badge } from "src/components/base/badge";
import ACCESS_RULE_CONTEXT from "src/context/internal/access-rule";
import { Text } from "src/components/base/text";
import { UUID } from "src/api/api_common";
import { ApiInternal } from "src/api/api_internal";
import { Divider } from "src/components/base/divider";
import DeleteExtraRecordDialog from "src/components/dialogs/delete-extra-record";
import { Link } from "src/components/base/link";
import { TagBadge } from "src/components/badges/tag";
import { StatusBadge } from "src/components/badges/status";
import { DeviceGroupBadge } from "src/components/badges/device-group";
import CreateDeviceGroupDialog from "src/components/dialogs/create-device-group";
import { CopyButton } from "src/components/base/copy-button";
import { ConfirmDialog } from "src/components/base/confirm-dialog";

/**
 * The properties for {@link GeneralDeviceView}
 */
export type GeneralDeviceViewProps = {};

/**
 * Lightweight section card — wraps content in the standard surface tokens
 * with a consistent header (title + optional actions).
 */
function SectionCard({
    title,
    actions,
    children,
}: {
    title: React.ReactNode;
    actions?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <section
            className={
                "flex flex-col gap-4 rounded-(--radius-card) bg-(--surface-card) p-5 shadow-(--shadow-card-sm) ring-1 ring-zinc-950/5 dark:ring-white/10"
            }
        >
            <header className={"flex items-center justify-between gap-3"}>
                <Subheading>{title}</Subheading>
                {actions && <div className={"flex items-center gap-1.5"}>{actions}</div>}
            </header>
            {children}
        </section>
    );
}

/**
 * A view for a single device
 */
function GeneralDeviceView(props: GeneralDeviceViewProps) {
    const [t] = useTranslation("admin-device-view");
    const [tg] = useTranslation();

    const settingsContext = React.useContext(SETTINGS_CONTEXT);
    const deviceContext = React.useContext(DEVICE_CONTEXT);
    const accessRuleCtx = React.useContext(ACCESS_RULE_CONTEXT);

    const router = useRouter();

    const device = deviceContext.device;
    const settings = settingsContext.settings;
    const data = Route.useLoaderData();

    // Popups
    const [openCreateTag, setOpenCreateTag] = React.useState(false);
    const [openRemoveDeviceTag, setOpenRemoveDeviceTag] = React.useState<SimpleTag>();
    const [openCreateDeviceGroup, setOpenCreateDeviceGroup] = React.useState(false);
    const [openRemoveFromDeviceGroup, setOpenRemoveFromDeviceGroup] = React.useState<SimpleDeviceGroup>();
    const [openDeleteExtraRecord, setOpenDeleteExtraRecord] = React.useState<UUID | undefined>(undefined);

    const [tagSearch, setTagSearch] = React.useState("");
    const [deviceGroupSearch, setDeviceGroupSearch] = React.useState("");

    /**
     * Add a device to a device group
     *
     * @param deviceGroup
     */
    const addToDeviceGroup = async (deviceGroup: UUID) => {
        await ApiInternal.devices.addToDeviceGroup(device.uuid, deviceGroup);
        deviceContext.reset();
    };

    /**
     * Remove a device from a device group
     *
     * @param deviceGroup
     */
    const removeFromDeviceGroup = async (deviceGroup: UUID) => {
        await ApiInternal.devices.removeFromDeviceGroup(device.uuid, deviceGroup);
        deviceContext.reset();
    };

    /**
     * Remove a tag from a device
     *
     * @param tag
     */
    const removeDeviceTag = async (tag: UUID) => {
        await ApiInternal.devices.removeDeviceTag(device.uuid, tag);
        deviceContext.reset();
    };

    /**
     * Add a tag to a device
     *
     * @param tag
     */
    const addDeviceTag = async (tag: UUID) => {
        await ApiInternal.devices.addDeviceTag(device.uuid, tag);
        deviceContext.reset();
    };

    /**
     * Open the device's Tailscale state in a new tab as JSON.
     */
    const dumpDeviceState = async () => {
        const result = await ApiInternal.devices.dumpDeviceState(device.uuid);
        const blob = new Blob([JSON.stringify(result)], { type: "application/javascript" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
    };

    /**
     * Download the device's Tailscale state as a JSON file.
     */
    const downloadDeviceState = async () => {
        const result = await ApiInternal.devices.dumpDeviceState(device.uuid);
        if (result.isErr) {
            notify.error(result.err.message);
            return;
        }
        const json = result.ok;
        if (json === null || json === undefined) {
            notify.error("Not Found");
            return;
        }

        downloadBlob(
            new Blob([JSON.stringify(json)], { type: "application/javascript" }),
            device.dnsname + "-targets.json",
        );
    };

    let filteredTags = device.tags
        ? accessRuleCtx.options.tags.filter((tag) => device.tags && !device.tags.some((at) => at.name === tag.name))
        : accessRuleCtx.options.tags;
    filteredTags =
        tagSearch === ""
            ? filteredTags
            : filteredTags.filter((x) => x.name.toLowerCase().includes(tagSearch.toLowerCase()));

    let filteredDeviceGroups = device.device_groups
        ? accessRuleCtx.options.device_groups.filter(
              (deviceGroup) => device.device_groups && !device.device_groups.some((at) => at.uuid === deviceGroup.uuid),
          )
        : [];
    filteredDeviceGroups = filteredDeviceGroups.filter((x) =>
        x.name.toLowerCase().includes(deviceGroupSearch.toLowerCase()),
    );

    const formatDate = (value: string | null | undefined) =>
        value
            ? new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })
            : "—";

    return (
        <div className={"grid grid-cols-1 gap-6 xl:grid-cols-5"}>
            {/* Left column — primary device facts */}
            <div className={"flex flex-col gap-6 xl:col-span-3"}>
                <SectionCard
                    title={t("heading.data")}
                    actions={
                        <>
                            <Button
                                plain={true}
                                onClick={() => dumpDeviceState()}
                                aria-label={t("button.dump-state")}
                                title={t("button.dump-state")}
                            >
                                <ArrowTopRightOnSquareIcon />
                            </Button>
                            <Button
                                plain={true}
                                onClick={() => downloadDeviceState()}
                                aria-label={t("button.dump-state")}
                                title={t("button.dump-state")}
                            >
                                <ArrowDownTrayIcon />
                            </Button>
                        </>
                    }
                >
                    <DescriptionList>
                        {device.user && (
                            <>
                                <DescriptionTerm>{t("label.owner")}</DescriptionTerm>
                                <DescriptionDetails>
                                    <Button
                                        className={"-translate-x-3"}
                                        plain={true}
                                        href={"/a/users/$userId/general"}
                                        params={{ userId: device.user.uuid }}
                                    >
                                        {device.user.display_name}
                                        <ArrowTopRightOnSquareIcon />
                                    </Button>
                                </DescriptionDetails>
                            </>
                        )}

                        <DescriptionTerm>{t("label.dns-names")}</DescriptionTerm>
                        <DescriptionDetails className={"flex flex-col gap-1"}>
                            {[device.dnsname, ...settings.headscale_domains.map((d) => `${device.dnsname}.${d}`)].map(
                                (name, idx) => (
                                    <span key={idx} className={"flex items-center gap-1.5"}>
                                        <span className={"font-mono text-xs"}>{name}</span>
                                        <CopyButton size="sm" value={name} />
                                    </span>
                                ),
                            )}
                            {data.list.length > 0 && (
                                <>
                                    <Divider className={"my-1"} />
                                    {data.list.map((dns) => (
                                        <div className={"flex items-center justify-between gap-2"} key={dns.uuid}>
                                            <Link
                                                className={"flex items-center gap-2 hover:cursor-pointer"}
                                                href={"/a/dns/extra-records"}
                                                search={{ search: dns.from_dns }}
                                            >
                                                <Badge color={"blue"} className={"w-fit"}>
                                                    {t("label.extra-dns")}
                                                </Badge>
                                                <span className={"w-50 truncate font-mono text-xs"}>
                                                    {dns.from_dns}
                                                </span>
                                            </Link>
                                            <Button
                                                plain={true}
                                                onClick={() => {
                                                    const record_uuid = dns.records.find(
                                                        (r) => r.device_uuid === device.uuid,
                                                    )?.uuid;
                                                    if (!record_uuid) return;
                                                    setOpenDeleteExtraRecord(record_uuid);
                                                }}
                                                className={"w-fit"}
                                                aria-label={tg("button.delete")}
                                            >
                                                <TrashIcon />
                                            </Button>
                                        </div>
                                    ))}
                                </>
                            )}
                        </DescriptionDetails>

                        {device.ipv4 && (
                            <>
                                <DescriptionTerm>{t("label.ipv4")}</DescriptionTerm>
                                <DescriptionDetails className={"flex items-center gap-1.5"}>
                                    <span className={"font-mono text-xs"}>{device.ipv4}</span>
                                    <CopyButton size="sm" value={device.ipv4} />
                                </DescriptionDetails>
                            </>
                        )}

                        {device.ipv6 && (
                            <>
                                <DescriptionTerm>{t("label.ipv6")}</DescriptionTerm>
                                <DescriptionDetails className={"flex items-center gap-1.5"}>
                                    <span className={"font-mono text-xs"}>{device.ipv6}</span>
                                    <CopyButton size="sm" value={device.ipv6} />
                                </DescriptionDetails>
                            </>
                        )}

                        <DescriptionTerm>{t("label.hostname")}</DescriptionTerm>
                        <DescriptionDetails>{device.hostname}</DescriptionDetails>

                        <DescriptionTerm>{t("label.first-seen")}</DescriptionTerm>
                        <DescriptionDetails>{formatDate(device.created_at)}</DescriptionDetails>

                        <DescriptionTerm>{t("label.last-seen")}</DescriptionTerm>
                        <DescriptionDetails>
                            <StatusBadge online={device.online} lastSeen={device.last_seen} />
                        </DescriptionDetails>

                        <DescriptionTerm>{t("label.expiry")}</DescriptionTerm>
                        <DescriptionDetails>{formatDate(device.expiry)}</DescriptionDetails>
                    </DescriptionList>
                </SectionCard>
            </div>

            {/* Right column — membership management (groups + tags) */}
            <div className={"flex flex-col gap-6 xl:col-span-2"}>
                {device.device_groups && (
                    <SectionCard title={t("label.device-groups")}>
                        {device.device_groups.length > 0 ? (
                            <div className={"flex flex-wrap gap-1.5"}>
                                {device.device_groups.map((deviceGroup) => (
                                    <DeviceGroupBadge
                                        key={deviceGroup.uuid}
                                        deviceGroup={deviceGroup}
                                        className={"flex !gap-1"}
                                        onRemove={() => setOpenRemoveFromDeviceGroup(deviceGroup)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Text className={"text-sm text-zinc-500 dark:text-zinc-400"}>
                                {t("label.no-device-groups")}
                            </Text>
                        )}

                        <Field>
                            <Label className={"sr-only"}>{t("label.add-device-groups")}</Label>
                            <div className={"flex items-center gap-2"}>
                                <StatelessCombobox<SimpleDeviceGroup | null>
                                    onChange={async (e) => {
                                        if (!e) return;
                                        await addToDeviceGroup(e.uuid);
                                    }}
                                    onClose={() => setDeviceGroupSearch("")}
                                    onQueryChange={(q) => setDeviceGroupSearch(q)}
                                >
                                    {filteredDeviceGroups.map((deviceGroup) => (
                                        <ComboboxOption value={deviceGroup} key={deviceGroup.uuid}>
                                            <Label>{`device-group:${deviceGroup.name}`}</Label>
                                        </ComboboxOption>
                                    ))}
                                </StatelessCombobox>
                                <Button
                                    outline={true}
                                    size="sm"
                                    className={"shrink-0"}
                                    onClick={() => setOpenCreateDeviceGroup(true)}
                                    aria-label={t("button.create-device-group")}
                                    title={t("button.create-device-group")}
                                >
                                    <PlusIcon />
                                </Button>
                            </div>
                        </Field>
                    </SectionCard>
                )}

                {device.tags && (
                    <SectionCard title={t("label.tags")}>
                        {device.tags.length > 0 ? (
                            <div className={"flex flex-wrap gap-1.5"}>
                                {device.tags.map((tag) => (
                                    <TagBadge
                                        key={tag.uuid}
                                        tag={tag}
                                        className={"flex !gap-1"}
                                        onRemove={() => setOpenRemoveDeviceTag(tag)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Text className={"text-sm text-zinc-500 dark:text-zinc-400"}>{t("label.no-tags")}</Text>
                        )}

                        <Field>
                            <Label className={"sr-only"}>{t("label.add-tags")}</Label>
                            <div className={"flex items-center gap-2"}>
                                <StatelessCombobox<SimpleTag | null>
                                    onChange={async (e) => {
                                        if (!e) return;
                                        await addDeviceTag(e.uuid);
                                    }}
                                    onClose={() => setTagSearch("")}
                                    onQueryChange={(q) => setTagSearch(q)}
                                >
                                    {filteredTags.map((tag) => (
                                        <ComboboxOption value={tag} key={tag.uuid}>
                                            <Label>{`tag:${tag.name}`}</Label>
                                        </ComboboxOption>
                                    ))}
                                </StatelessCombobox>
                                <Button
                                    outline={true}
                                    size="sm"
                                    className={"shrink-0"}
                                    onClick={() => setOpenCreateTag(true)}
                                    aria-label={t("button.create-tag")}
                                    title={t("button.create-tag")}
                                >
                                    <PlusIcon />
                                </Button>
                            </div>
                        </Field>
                    </SectionCard>
                )}
            </div>

            {/* Dialogs */}
            <Suspense>
                <CreateDeviceGroupDialog
                    isOpen={openCreateDeviceGroup}
                    onSubmit={async (deviceGroupUuid) => {
                        await addToDeviceGroup(deviceGroupUuid);
                    }}
                    onClose={() => setOpenCreateDeviceGroup(false)}
                />
            </Suspense>

            <ConfirmDialog
                open={!!openRemoveFromDeviceGroup}
                onClose={() => setOpenRemoveFromDeviceGroup(undefined)}
                onConfirm={async () => {
                    if (!openRemoveFromDeviceGroup) return;
                    await removeFromDeviceGroup(openRemoveFromDeviceGroup.uuid);
                    setOpenRemoveFromDeviceGroup(undefined);
                }}
                title={
                    openRemoveFromDeviceGroup
                        ? t("heading.remove-device-group", { deviceGroup: openRemoveFromDeviceGroup.name })
                        : ""
                }
                description={t("description.remove-device-group")}
                confirmLabel={t("button.remove-device-group")}
            />

            <ConfirmDialog
                open={!!openRemoveDeviceTag}
                onClose={() => setOpenRemoveDeviceTag(undefined)}
                onConfirm={async () => {
                    if (!openRemoveDeviceTag) return;
                    await removeDeviceTag(openRemoveDeviceTag.uuid);
                    setOpenRemoveDeviceTag(undefined);
                }}
                title={openRemoveDeviceTag ? t("heading.remove-tag", { tag: openRemoveDeviceTag.name }) : ""}
                description={t("description.remove-tag")}
                confirmLabel={t("button.remove-tag")}
            />

            <Suspense>
                <CreateTagDialog
                    directory_uuid={undefined}
                    isOpen={openCreateTag}
                    onClose={() => setOpenCreateTag(false)}
                    onSubmit={async ({ tagUuid }) => {
                        setOpenCreateTag(false);
                        await addDeviceTag(tagUuid);
                    }}
                />

                <DeleteExtraRecordDialog
                    record={openDeleteExtraRecord}
                    isOpen={!!openDeleteExtraRecord}
                    onClose={() => setOpenDeleteExtraRecord(undefined)}
                    onDelete={() => {
                        setOpenDeleteExtraRecord(undefined);
                        router.invalidate();
                    }}
                />
            </Suspense>
        </div>
    );
}

/**
 * Download a blob
 *
 * @param blob
 * @param filename
 */
function downloadBlob(blob: Blob, filename: string) {
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.setAttribute("href", url);
    a.setAttribute("download", filename);
    a.click();
    URL.revokeObjectURL(url);
}

export const Route = createFileRoute("/_admin/a/devices/$deviceId/_device/general")({
    component: GeneralDeviceView,
    loader: async ({ params }) => await ApiInternal.dns.getDeviceExtraDns(params.deviceId),
});
